import { useState } from 'react';
import { supabase } from '../lib/supabase';

export default function ExpenseForm({ groupId, members, onExpenseAdded }) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [splitType, setSplitType] = useState('equal');
  const [paidBy, setPaidBy] = useState('');  // Initialize as empty string
  const [currency, setCurrency] = useState('USD');
  const [shares, setShares] = useState(
    members.reduce((acc, member) => ({
      ...acc,
      [member.email]: splitType === 'equal' ? 1 : 0
    }), {})
  );

  // Add function to calculate total shares
  const calculateTotalShares = () => {
    return Object.values(shares).reduce((sum, share) => sum + Number(share || 0), 0);
  };

  // Add function to calculate individual amounts
  const calculateIndividualAmounts = () => {
    const totalShares = calculateTotalShares();
    if (!amount || totalShares === 0) return {};

    return members.reduce((acc, member) => ({
      ...acc,
      [member.email]: splitType === 'equal' 
        ? Number(amount) / members.length 
        : (Number(shares[member.email] || 0) / totalShares) * Number(amount)
    }), {});
  };

  // Update handleSubmit to include currency
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!amount || Number(amount) <= 0) {
        throw new Error('Please enter a valid amount');
      }

      if (!paidBy) {
        throw new Error('Please select who paid');
      }

      const totalShares = Object.values(shares).reduce((sum, share) => sum + Number(share), 0);
      
      if (splitType === 'percentage' && totalShares !== 100) {
        throw new Error('Percentages must total 100%');
      }

      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const individualAmounts = calculateIndividualAmounts();
      const newExpense = {
        group_id: groupId,
        description,
        amount: Number(amount),
        currency,
        category,
        created_by: session.user.id,
        splits: members.map(member => ({
          user_email: member.email,
          user_name: member.name,
          amount: individualAmounts[member.email]
        })),
        paid_by: paidBy,
        split_type: splitType
      };

      const { data, error } = await supabase
        .from('expenses_table')
        .insert(newExpense)
        .select()
        .single();

      if (error) throw error;

      onExpenseAdded(data);
      
      // Reset form
      setAmount('');
      setDescription('');
      setCategory('');
      setSplitType('equal');
      setPaidBy('');
      setShares(members.reduce((acc, member) => ({
        ...acc,
        [member.email]: 1
      }), {}));

    } catch (error) {
      console.error('Error adding expense:', error);
      alert(error.message || 'Failed to add expense');
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow max-w-2xl mx-auto">
      <h3 className="text-lg font-medium mb-4">Add Expense</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Description field */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            >
              <option value="USD">USD</option>
              <option value="INR">INR</option>
              <option value="AUD">AUD</option>
            </select>
          </div>
        </div>

        {/* Category field */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          >
            <option value="">Select Category</option>
            <option value="food">Food</option>
            <option value="transportation">Transportation</option>
            <option value="accommodation">Accommodation</option>
            <option value="activities">Activities</option>
            <option value="shopping">Shopping</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Paid By field */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Paid By</label>
          <select
            value={paidBy}
            onChange={(e) => {
              const selectedEmail = e.target.value;
              setPaidBy(selectedEmail);
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          >
            <option value="">Select who paid</option>
            {members.map((member) => (
              <option key={member.email} value={member.email}>
                {member.name}
              </option>
            ))}
          </select>
        </div>

        {/* Split Type field */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Split Type</label>
          <select
            value={splitType}
            onChange={(e) => setSplitType(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="equal">Equal Split</option>
            <option value="unequal">Unequal Split</option>
            <option value="percentage">Percentage Split</option>
          </select>
        </div>

        {/* Share allocation fields */}
        {splitType !== 'equal' && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              {splitType === 'percentage' ? 'Percentages' : 'Shares'}
            </label>
            {members.map((member) => (
              <div key={member.email} className="flex items-center gap-2">
                <span>{member.name}</span>
                <input
                  type="number"
                  value={shares[member.email]}
                  onChange={(e) => setShares({
                    ...shares,
                    [member.email]: e.target.value
                  })}
                  className="w-24 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span>{splitType === 'percentage' ? '%' : 'shares'}</span>
              </div>
            ))}
            {/* Show real-time share calculations */}
            {amount && (
              <div className="mt-2 text-xs text-gray-600">
                <p className="font-medium">Share Distribution:</p>
                {members.map(member => {
                  const amounts = calculateIndividualAmounts();
                  return (
                    <div key={member.email} className="flex justify-between">
                      <span>{member.name}:</span>
                      <span>{currency} {amounts[member.email]?.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Add Expense
        </button>
      </form>
    </div>
  );
}
